// src/components/CalendarView.js
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaClock, FaUser, FaAlignLeft, FaTimes, FaMapMarkerAlt, FaFilter, FaSync, FaEnvelope } from 'react-icons/fa';
import '../App.css';

moment.locale('it');
const localizer = momentLocalizer(moment);

const spaceColors = {
    "Sala Conferenze \"Danilo Dolci\"": "#e53935",
    "Sala Principale": "#1e88e5",
    "Cortile": "#fb8c00",
    "default": "#6c7d79ff"
};

const daysOfWeek = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// --- TRADUZIONI ITALIANE ---
const calendarMessages = {
    date: 'Data',
    time: 'Ora',
    event: 'Evento',
    allDay: 'Tutto il giorno',
    week: 'Settimana',
    work_week: 'Settimana Lavorativa',
    day: 'Giorno',
    month: 'Mese',
    previous: 'Indietro',
    next: 'Avanti',
    yesterday: 'Ieri',
    tomorrow: 'Domani',
    today: 'Oggi',
    agenda: 'Agenda',
    noEventsInRange: 'Nessun evento in questo periodo.',
    showMore: total => `+${total} altri`
};

const CustomEventComponent = ({ event }) => {
    const resources = event.resource ? event.resource.split(',').map(r => r.trim()) : [];
    let backgroundStyle = {};

    if (resources.length > 1) {
        const colors = resources.map(r => spaceColors[r] || spaceColors["default"]);
        const segmentSize = 100 / colors.length;
        const gradientStops = colors.map((color, index) => {
            const start = index * segmentSize;
            const end = (index + 1) * segmentSize;
            return `${color} ${start}%, ${color} ${end}%`;
        }).join(', ');
        backgroundStyle.background = `linear-gradient(135deg, ${gradientStops})`;
    } else {
        backgroundStyle.backgroundColor = spaceColors[event.resource] || spaceColors["default"];
    }

    return (
        <div style={{
            ...backgroundStyle,
            // MODIFICA 1: Ridotto il padding verticale per guadagnare spazio
            padding: '1px 5px',
            borderRadius: '3px',
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            color: 'white',
            fontSize: '10px', // Leggermente più piccolo per evitare wrap
            fontWeight: '600',
            letterSpacing: '0.3px',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
            whiteSpace: 'nowrap',
        }}>
            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {event.title.split('(')[0]}
            </span>
        </div>
    );
};

function CalendarView({ onBack, user, userData }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewDate, setViewDate] = useState(new Date());
    const [errorNotice, setErrorNotice] = useState(null);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [dayListModal, setDayListModal] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState([]);

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                setLoading(true);

                const usersMap = {};
                try {
                    const usersSnapshot = await getDocs(collection(db, "users"));
                    usersSnapshot.forEach((doc) => {
                        const userData = doc.data();
                        if (userData.email) {
                            const fullName = (userData.firstName && userData.lastName)
                                ? `${userData.firstName} ${userData.lastName}`
                                : (userData.userName || "Utente");
                            usersMap[userData.email] = fullName;
                        }
                    });
                } catch (userErr) {
                    console.warn("Impossibile caricare l'elenco utenti (normale se non autenticati o non admin):", userErr);
                }

                const q = query(collection(db, "bookings"), where("status", "==", "confirmed"));
                const querySnapshot = await getDocs(q);
                let loadedEvents = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const organizerName = usersMap[data.userEmail] || data.userName || (user ? data.userEmail : "Evento Confermato");

                    const baseEvent = {
                        resource: data.spaceId,
                        userEmail: data.userEmail,
                        userName: organizerName,
                        desc: data.description,
                        isRecursive: false,
                        recurrence: null
                    };

                    const isRecurring = (data.bookingType === 'recurring' || data.isMultiDay === true) && data.recurrenceDays && data.recurrenceDays.length > 0;

                    if (isRecurring) {
                        let current = new Date(data.startDate);
                        const end = new Date(data.endDate || data.startDate);
                        end.setHours(23, 59, 59, 999);

                        const periodStartStr = data.startDate;
                        const periodEndStr = data.endDate || data.startDate;

                        while (current <= end) {
                            const currentDateStr = moment(current).format('YYYY-MM-DD');
                            if (data.excludedDates && data.excludedDates.includes(currentDateStr)) {
                                current.setDate(current.getDate() + 1);
                                continue;
                            }
                            if (data.recurrenceDays.includes(current.getDay())) {
                                const evtStart = new Date(current);
                                const [sHour, sMin] = data.startTime.split(':');
                                evtStart.setHours(parseInt(sHour), parseInt(sMin), 0);
                                const evtEnd = new Date(current);
                                const [eHour, eMin] = data.endTime.split(':');
                                evtEnd.setHours(parseInt(eHour), parseInt(eMin), 0);

                                loadedEvents.push({
                                    ...baseEvent,
                                    id: `${doc.id}_${current.getTime()}`,
                                    title: `${data.eventName} (${data.spaceId})`,
                                    start: evtStart,
                                    end: evtEnd,
                                    allDay: false,
                                    isRecursive: true,
                                    recurrence: data.recurrenceDays,
                                    recurrenceStartStr: periodStartStr,
                                    recurrenceEndStr: periodEndStr
                                });
                            }
                            current.setDate(current.getDate() + 1);
                        }
                    } else {
                        const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
                        const endDateTime = new Date(`${data.endDate || data.startDate}T${data.endTime}`);
                        loadedEvents.push({
                            ...baseEvent,
                            id: doc.id,
                            title: `${data.eventName} (${data.spaceId})`,
                            start: startDateTime,
                            end: endDateTime,
                            allDay: false
                        });
                    }
                });
                setEvents(loadedEvents);
            } catch (error) { 
                console.error("Errore recupero prenotazioni:", error); 
                if (error.code === 'permission-denied') {
                    setErrorNotice("Le regole di sicurezza di Firestore non consentono ancora la lettura pubblica delle prenotazioni. È necessario aggiornare le regole su Firebase Console.");
                }
            }
            setLoading(false);
        };
        fetchBookings();
    }, [user]);

    const toggleFilter = (spaceName) => {
        setSelectedFilters(prev => prev.includes(spaceName) ? prev.filter(f => f !== spaceName) : [...prev, spaceName]);
    };

    const filteredEvents = selectedFilters.length > 0
        ? events.filter(event => selectedFilters.some(filterSpace => event.resource.includes(filterSpace)))
        : events;

    const eventStyleGetter = (event) => {
        return {
            // MODIFICA 2: Rimosso marginBottom (o settato a 1px) per avvicinare gli eventi
            style: {
                backgroundColor: 'transparent',
                border: 'none',
                padding: '0px 1px',
                borderRadius: '0px',
                opacity: 1,
                color: 'transparent',
                display: 'block',
                boxShadow: 'none',
                marginBottom: '1px' // Molto più compatto
            }
        };
    };

    const handleSelectSlot = (input) => {
        setSelectedEvent(null);
        const dateObj = input.start || input;
        const clickedDate = moment(dateObj);
        const eventsForDay = filteredEvents.filter(evt =>
            moment(evt.start).isSame(clickedDate, 'day') ||
            (moment(clickedDate).isBetween(evt.start, evt.end, 'day', '[]'))
        );
        setDayListModal({ date: dateObj, events: eventsForDay });
    };

    const navigateDay = (direction) => {
        if (!dayListModal) return;
        const current = moment(dayListModal.date);
        const newDate = direction === 'PREV' ? current.subtract(1, 'days') : current.add(1, 'days');
        const eventsForNewDay = filteredEvents.filter(evt =>
            moment(evt.start).isSame(newDate, 'day') ||
            (moment(newDate).isBetween(evt.start, evt.end, 'day', '[]'))
        );
        setSelectedEvent(null);
        setDayListModal({ date: newDate.toDate(), events: eventsForNewDay });
    };

    const arrowBtnStyle = {
        width: '42px', height: '42px', borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.75)', backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)',
        transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)', color: '#333'
    };

    const CustomToolbar = (toolbar) => {
        const goToBack = () => toolbar.onNavigate('PREV');
        const goToNext = () => toolbar.onNavigate('NEXT');
        const label = () => { const date = moment(toolbar.date); return <span style={{ textTransform: 'capitalize' }}>{date.format('MMMM YYYY')}</span>; };

        return (
            <div className="admin-cal-toolbar" style={{ marginBottom: '25px' }}>
                <button onClick={goToBack} className="admin-cal-nav-btn" onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }} title="Mese precedente"><FaChevronLeft size={16} /></button>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#333', letterSpacing: '-0.5px', textAlign: 'center', flex: 1, maxWidth: '260px' }}>{label()}</div>
                <button onClick={goToNext} className="admin-cal-nav-btn" onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }} title="Mese successivo"><FaChevronRight size={16} /></button>
            </div>
        );
    };

    const getDetailHeaderBackground = (event) => {
        if (!event || !event.resource) return { background: '#ccc' };
        const resources = event.resource.split(',').map(r => r.trim());
        if (resources.length > 1) {
            const colors = resources.map(r => spaceColors[r] || spaceColors["default"]);
            const segmentSize = 100 / colors.length;
            const gradientStops = colors.map((color, index) => {
                const start = index * segmentSize;
                const end = (index + 1) * segmentSize;
                return `${color} ${start}%, ${color} ${end}%`;
            }).join(', ');
            return { background: `linear-gradient(135deg, ${gradientStops})` };
        } else {
            return { backgroundColor: spaceColors[event.resource] || spaceColors["default"] };
        }
    };

    return (
        <div style={{

            minHeight: '100vh',
            width: '100%',
            padding: '20px', fontFamily: "'Poppins', sans-serif",
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            boxSizing: 'border-box'
        }}>

            <div className="glass-panel" style={{
                width: '98%', maxWidth: '1400px',
                height: 'auto', minHeight: '90vh',
                padding: '20px', borderRadius: '30px',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)', position: 'relative',
                background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255,255,255,0.5)',
                boxSizing: 'border-box'
            }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexShrink: 0, flexWrap: 'wrap', gap: '15px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: 'white', padding: '8px 18px', borderRadius: '30px',
                            border: 'none', cursor: 'pointer', fontSize: '14px', color: '#222', fontWeight: 'bold',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        <FaArrowLeft /> Torna alla Home
                    </button>
                    <h1 style={{ margin: 0, color: '#333', fontSize: '18px', opacity: 0.8, fontWeight: '600' }}>Disponibilità Sale</h1>
                </div>

                <div className="responsive-calendar-container" style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.5)', borderRadius: '24px',
                    padding: '10px', overflow: 'hidden', color: '#444',
                    transition: 'background 0.3s', display: 'flex', flexDirection: 'column',
                    boxSizing: 'border-box'
                }}>
                    {errorNotice && (
                        <div style={{
                            background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba',
                            padding: '12px 18px', borderRadius: '14px', marginBottom: '12px', fontSize: '13px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
                        }}>
                            <span>⚠️ {errorNotice}</span>
                        </div>
                    )}
                    {loading ? <p style={{ textAlign: 'center', marginTop: '20%' }}>Caricamento...</p> : (
                        <Calendar
                            localizer={localizer}
                            events={filteredEvents}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: '100%', width: '100%', fontFamily: "'Poppins', sans-serif" }}
                            views={['month']}
                            defaultView='month'
                            date={viewDate}
                            onNavigate={(date) => setViewDate(date)}
                            selectable={true}
                            onSelectSlot={handleSelectSlot}
                            onDrillDown={handleSelectSlot}
                            longPressThreshold={90}
                            messages={calendarMessages}
                            onShowMore={(events, date) => handleSelectSlot({ start: date })}
                            components={{ toolbar: CustomToolbar, event: CustomEventComponent }}
                            eventPropGetter={eventStyleGetter}
                            popup={true}
                            // MODIFICA 3: dayMaxEvents controlla quanti eventi mostrare. 
                            // Se metti un numero (es. 5), forzi la visualizzazione di 5 eventi.
                            // Se lo lasci a 'true', calcola in automatico.
                            // Dato che abbiamo ridotto le dimensioni, 'true' dovrebbe mostrarne di più ora.
                            dayMaxEvents={true}
                            formats={{ monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'MMMM YYYY', culture) }}
                        />
                    )}
                </div>

                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <div style={{ fontSize: '11px', color: '#292929ff', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '5px', textTransform: 'uppercase', letterSpacing: '1px' }}><FaFilter /> Filtra Spazi</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {Object.entries(spaceColors).map(([name, color]) => {
                            if (name === 'default') return null;
                            const isSelected = selectedFilters.includes(name);
                            const isAnyFilterActive = selectedFilters.length > 0;
                            const opacity = isAnyFilterActive && !isSelected ? 0.4 : 1;
                            const bgColor = isSelected ? color : '#f5f5f5';
                            const textColor = isSelected ? 'white' : '#555';
                            return (
                                <div key={name} onClick={() => toggleFilter(name)} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', background: bgColor, color: textColor, padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.2s', opacity: opacity, border: isSelected ? `1px solid ${color}` : '1px solid transparent', transform: isSelected ? 'translateY(-1px)' : 'none', boxShadow: isSelected ? `0 4px 10px ${color}40` : 'none' }}>
                                    {!isSelected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }}></div>}
                                    {name}
                                </div>
                            )
                        })}
                        {selectedFilters.length > 0 && (<div onClick={() => setSelectedFilters([])} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '600', background: 'transparent', color: '#666', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', border: '1px solid currentColor', opacity: 0.7 }}><FaTimes /> Reset</div>)}
                    </div>
                </div>

                {/* Modal — renderizzato come portal fuori dal DOM del calendario */}
                {dayListModal && ReactDOM.createPortal(
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(6px)' }} onClick={() => { setDayListModal(null); setSelectedEvent(null); }}>
                        <div className="modal-animate" style={{
                            background: 'white',
                            width: '92%',
                            maxWidth: selectedEvent ? '700px' : '480px',
                            borderRadius: '24px', padding: '0',
                            boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
                            position: 'relative', color: '#222', overflow: 'hidden',
                            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                            transition: 'max-width 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                        }} onClick={(e) => e.stopPropagation()}>

                            {/* HEADER: una sola riga con < data > × */}
                            <div style={{ position: 'relative', padding: '14px 16px', borderBottom: '1px solid #eee', background: '#f8f9fa', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button onClick={() => navigateDay('PREV')} style={{ ...arrowBtnStyle, width: '32px', height: '32px', flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = 'white'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; }}><FaChevronLeft size={12} /></button>
                                    <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                        <span style={{ fontSize: '9px', textTransform: 'uppercase', color: '#888', fontWeight: '700', display: 'block', lineHeight: '1', marginBottom: '2px' }}>EVENTI DEL</span>
                                        <div style={{ fontSize: '16px', fontWeight: '800', color: '#222', lineHeight: '1.2' }}>{moment(dayListModal.date).format('D MMMM YYYY')}</div>
                                    </div>
                                    <button onClick={() => navigateDay('NEXT')} style={{ ...arrowBtnStyle, width: '32px', height: '32px', flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = 'white'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; }}><FaChevronRight size={12} /></button>
                                </div>
                                <button onClick={() => { setDayListModal(null); setSelectedEvent(null); }} style={{ position: 'absolute', right: '12px', border: 'none', background: 'rgba(0,0,0,0.07)', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', cursor: 'pointer', color: '#666', flexShrink: 0 }}><FaTimes /></button>
                            </div>

                            {/* BODY: lista + dettaglio — riga su desktop, colonna su mobile */}
                            <div className={`day-modal-body${selectedEvent ? ' day-modal-body--split' : ''}`}>
                                {/* Lista eventi */}
                                <div className="day-modal-list" style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {dayListModal.events.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>Nessun evento in questa data</div>
                                    ) : (
                                        dayListModal.events.map((evt, idx) => {
                                            const res = evt.resource.split(',').map(r => r.trim());
                                            let bg = {};
                                            if (res.length > 1) {
                                                const c = res.map(r => spaceColors[r] || spaceColors["default"]);
                                                const p = 100 / c.length;
                                                bg.background = `linear-gradient(135deg, ${c.map((x, i) => `${x} ${i * p}%, ${x} ${(i + 1) * p}%`).join(',')})`;
                                            } else {
                                                bg.backgroundColor = spaceColors[evt.resource] || spaceColors["default"];
                                            }
                                            const isActive = selectedEvent && selectedEvent.id === evt.id;
                                            return (
                                                <div key={idx} onClick={() => setSelectedEvent(isActive ? null : evt)}
                                                    style={{
                                                        background: isActive ? '#f0f7ff' : 'white',
                                                        borderColor: isActive ? '#007bff' : '#eee',
                                                        borderRadius: '12px', padding: '12px',
                                                        boxShadow: isActive ? '0 4px 12px rgba(0,123,255,0.15)' : '0 2px 8px rgba(0,0,0,0.05)',
                                                        borderWidth: '1px', borderStyle: 'solid', cursor: 'pointer', transition: 'all 0.2s',
                                                        display: 'flex', alignItems: 'center', gap: '12px'
                                                    }}
                                                >
                                                    <div style={{ ...bg, width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0 }}></div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '700', color: isActive ? '#007bff' : '#333' }}>{evt.title.split('(')[0]}</div>
                                                        <div style={{ fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{moment(evt.start).format('HH:mm')} - {moment(evt.end).format('HH:mm')}</span>
                                                            <span>{evt.resource.includes(',') ? 'Multi Spazio' : evt.resource}</span>
                                                        </div>
                                                    </div>
                                                    <FaChevronRight color={isActive ? '#007bff' : '#ccc'} size={10} style={{ transform: isActive ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Dettaglio evento */}
                                {selectedEvent && (
                                    <div className="day-modal-detail" style={{ animation: 'floatUp 0.25s ease-out' }}>
                                        <div style={{ ...getDetailHeaderBackground(selectedEvent), padding: '16px 20px', position: 'relative' }}>
                                            <button onClick={(e) => { e.stopPropagation(); setSelectedEvent(null); }} style={{ position: 'absolute', top: '12px', right: '12px', border: 'none', background: 'rgba(0,0,0,0.2)', width: '28px', height: '28px', borderRadius: '50%', fontSize: '13px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes /></button>
                                            <h2 style={{ margin: '0 0 4px 0', color: 'white', fontSize: '20px', fontWeight: '800', paddingRight: '30px' }}>{selectedEvent.title.split('(')[0]}</h2>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}><FaMapMarkerAlt /> {selectedEvent.resource}</div>
                                        </div>
                                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px', background: 'white' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                                                <div style={{ background: '#f5f5f5', padding: '9px', borderRadius: '12px' }}><FaUser color='#555' size={18} /></div>
                                                <div>
                                                    <span style={{ fontSize: '12px', color: '#888', display: 'block' }}>Richiedente</span>
                                                    <strong style={{ fontSize: '14px', display: 'block' }}>{selectedEvent.userName}</strong>
                                                    {selectedEvent.userEmail && user && (
                                                        <div style={{ fontSize: '12px', color: '#007bff', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}><FaEnvelope size={10} /> {selectedEvent.userEmail}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                                                <div style={{ background: '#f5f5f5', padding: '9px', borderRadius: '12px' }}><FaClock color='#555' size={18} /></div>
                                                <div>
                                                    <span style={{ fontSize: '12px', color: '#888', display: 'block' }}>Orario</span>
                                                    <strong style={{ fontSize: '14px', display: 'block', color: '#222' }}>{moment(selectedEvent.start).format('HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}</strong>
                                                    <span style={{ fontSize: '12px', color: '#888' }}>{moment(selectedEvent.start).format('DD MMMM YYYY')}</span>
                                                </div>
                                            </div>
                                            {selectedEvent.isRecursive && selectedEvent.recurrence && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                                                    <div style={{ background: '#f5f5f5', padding: '9px', borderRadius: '12px' }}><FaSync color='#6610f2' size={18} /></div>
                                                    <div>
                                                        <span style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Ripetizione</span>
                                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                                            {selectedEvent.recurrence.map((dayIdx) => (
                                                                <span key={dayIdx} style={{ fontSize: '11px', fontWeight: '700', background: '#e9f5ff', color: '#007bff', padding: '3px 8px', borderRadius: '8px' }}>{daysOfWeek[dayIdx]}</span>
                                                            ))}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#888' }}>Periodo: <span style={{ color: '#333', fontWeight: '500' }}>{moment(selectedEvent.recurrenceStartStr).format('DD MMM YYYY')} - {moment(selectedEvent.recurrenceEndStr).format('DD MMM YYYY')}</span></div>
                                                    </div>
                                                </div>
                                            )}
                                            {selectedEvent.desc && (
                                                <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '12px', border: '1px solid #eee' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', marginBottom: '6px', fontSize: '12px', color: '#555' }}><FaAlignLeft /> Descrizione</div>
                                                    <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#444' }}>{selectedEvent.desc}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    , document.body)}


            </div>
        </div>
    );
}

export default CalendarView;
